/**
 * The brief conversation (docs/SPEC.md §5): what was said so far, the open
 * question, and what TrueReel is doing. The last turn's error is shown as the
 * API's sentence (`brief_state.sentence`), never as a code.
 */
import type { AnswerBody } from "../api/brief";
import type { BriefState, BriefTurn } from "../api/ads";
import { CONVERSATION } from "../copy/brief";
import { Button } from "../ui";
import { Question } from "./Question";

function Turn({ turn }: { turn: BriefTurn }) {
  const mine = turn.role === "user";
  const words = turn.question?.text || turn.text;
  if (!words) return null;
  return (
    <li
      className={[
        "flex flex-col gap-0.5",
        mine ? "items-end text-right" : "items-start",
      ].join(" ")}
      data-testid={mine ? "turn-user" : "turn-model"}
    >
      <span className="text-xs font-semibold text-slate-500">
        {mine ? CONVERSATION.you : CONVERSATION.trueReel}
        {turn.kind === "edit"
          ? ` · ${CONVERSATION.edited}`
          : turn.kind === "accept"
            ? ` · ${CONVERSATION.accepted}`
            : ""}
      </span>
      <span
        className={[
          "max-w-[36rem] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm",
          mine
            ? "bg-brand text-white"
            : "border border-slate-200 bg-white text-slate-800",
        ].join(" ")}
      >
        {words}
      </span>
    </li>
  );
}

export function Conversation({
  state,
  sending,
  onAnswer,
}: {
  state: BriefState;
  /** An answer is in flight. */
  sending: boolean;
  onAnswer: (body: AnswerBody) => void;
}) {
  const busy = state.working || sending;
  const open = state.final ? null : state.question;
  // The open question is drawn as a question, not again as a turn.
  const turns =
    open && state.turns.at(-1)?.question
      ? state.turns.slice(0, -1)
      : state.turns;
  return (
    <section
      aria-label={CONVERSATION.title}
      className="flex flex-col gap-4"
      data-testid="conversation"
    >
      {turns.length > 0 ? (
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {turns.map((turn, i) => (
            <Turn key={`${i}-${turn.at ?? ""}`} turn={turn} />
          ))}
        </ol>
      ) : null}
      {open ? (
        <Question
          key={`${state.questions_asked}-${open.text}`}
          question={open}
          number={Math.max(1, state.questions_asked)}
          busy={busy}
          onAnswer={onAnswer}
        />
      ) : null}
      {state.working ? (
        <p
          role="status"
          className="m-0 flex items-center gap-2 text-sm text-slate-600"
        >
          <span
            className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand"
            aria-hidden="true"
          />
          {state.turns.length === 0
            ? CONVERSATION.starting
            : CONVERSATION.thinking}
        </p>
      ) : null}
      {state.sentence ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {state.sentence}
        </p>
      ) : null}
      {!state.final && !open && !state.working ? (
        // A turn that ended without a question (an error, or the model
        // finished without one while the brief is not final yet).
        <div>
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => onAnswer({ you_decide: true })}
          >
            {CONVERSATION.youDecide}
          </Button>
        </div>
      ) : null}
      {state.final ? (
        <p className="m-0 text-sm font-semibold text-slate-700">
          {CONVERSATION.final}
        </p>
      ) : null}
    </section>
  );
}
